import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { queryParamsModule } from '@/modules/queryParams';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useOnboarding } from '@/pages/Onboarding/context';
import { LONELINESS_OPTIONS } from '@/pages/Onboarding/options/Loneliness/LONELINESS_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataKeyType, LayoutDataType } from '../../types';

import './Loneliness.scss';

export const OnboardingReplikaLoneliness: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Loneliness Survey',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const { refreshUser } = useAccount();
  const loneliness = form.watch('loneliness');

  const handleChange = (
    option: OnbOptionType<LayoutDataType['loneliness']>,
  ) => {
    queryParamsModule.setParam<LayoutDataKeyType>('loneliness', option.id);

    const { name, loneliness } = form.getValues();

    const hasUserLoneliness = loneliness !== 'never';

    userService
      .createOrUpdate({
        name,
        onboarding_data: {
          loneliness: option.title,
        },
      })
      .then(() => {
        refreshUser();
      });

    if (hasUserLoneliness) {
      nextStepTimeout(wizard.nextStep);
    } else {
      nextStepTimeout(() => wizard.goToStep('personalizing_feed'));
    }
  };

  const handleBackStep = () => {
    wizard.goToStep('outfit');
  };

  return (
    <OnboardingPage className="onboarding-loneliness">
      <OnboardingNavigation onBack={handleBackStep} />

      <OnboardingTitle className="title">
        Reflecting on loneliness: do you ever feel it?
      </OnboardingTitle>

      <IonContent>
        <OnboardingOptionsList>
          {LONELINESS_OPTIONS.map((option) => (
            <OnboardingOptionItem
              checked={loneliness === option.id}
              control={form.control}
              option={option}
              name="loneliness"
              key={option.id}
              onChange={() => handleChange(option)}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>
    </OnboardingPage>
  );
};
